import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  ReferenceSidebarCollapseButton,
  ReferenceSidebarSplitter,
} from './ReferenceSidebarChrome';
import { useResizablePanel } from './useResizablePanel';

export type ReferenceNavItem = {
  id: string;
  label: string;
  description?: string;
  /** Sidebar section heading. Consecutive items with the same group are shown together. */
  group?: string;
  /** Right pane should fill height (split / nested workspaces). */
  fillHeight?: boolean;
  /** If set, hide from nav when the user lacks view access. */
  permissionModuleKey?: string;
};

export type ReferenceCategoryWorkspaceProps = {
  /** Base path without query, e.g. `/parameters/general`. */
  basePath: string;
  eyebrow: string;
  items: ReferenceNavItem[];
  defaultSection: string;
  renderPanel: (sectionId: string) => React.ReactNode;
};

/**
 * Shared left-nav + right-panel shell for Reference Data category hubs.
 * Left rail is resizable and collapsible.
 */
export const ReferenceCategoryWorkspace: React.FC<ReferenceCategoryWorkspaceProps> = ({
  basePath,
  eyebrow,
  items,
  defaultSection,
  renderPanel,
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const {
    splitRef,
    panelWidth,
    collapsed,
    setCollapsed,
    panelResizing,
    startResize,
    minWidth,
    maxWidth,
  } = useResizablePanel({ storageKey: `ref-workspace:${basePath}` });

  const visibleItems = useMemo(
    () =>
      items.filter((item) => {
        if (!item.permissionModuleKey) return true;
        return can(item.permissionModuleKey, 'view');
      }),
    [items, can],
  );

  const validIds = useMemo(() => new Set(visibleItems.map((i) => i.id)), [visibleItems]);
  const fallback = visibleItems[0]?.id ?? defaultSection;

  const resolve = (raw: string | null | undefined) =>
    raw && validIds.has(raw) ? raw : fallback;

  const section = resolve(params.get('section'));

  const setSection = (next: string) => {
    navigate(`${basePath}?section=${next}`, { replace: true });
  };

  useEffect(() => {
    const raw = params.get('section');
    const resolved = resolve(raw);
    if (raw !== resolved && resolved) {
      navigate(`${basePath}?section=${resolved}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolve depends on visibleItems
  }, [params, navigate, basePath, fallback, validIds]);

  const activeMeta = visibleItems.find((i) => i.id === section) ?? visibleItems[0];
  const fillHeight = Boolean(activeMeta?.fillHeight);
  const navSections = useMemo(() => {
    const sections: { group: string | null; items: ReferenceNavItem[] }[] = [];
    for (const item of visibleItems) {
      const group = item.group ?? null;
      const last = sections[sections.length - 1];
      if (last && last.group === group) last.items.push(item);
      else sections.push({ group, items: [item] });
    }
    return sections;
  }, [visibleItems]);

  if (!visibleItems.length) {
    return (
      <div
        className={cn(
          'flex h-full min-h-[240px] items-center justify-center rounded-2xl border',
          colors.border,
          colors.surface,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>No reference lists available for your account.</p>
      </div>
    );
  }

  return (
    <div
      ref={splitRef}
      className={cn(
        'flex h-full min-h-0 overflow-hidden rounded-2xl border shadow-sm',
        colors.surface,
        colors.border,
        panelResizing && 'select-none',
      )}
    >
      {collapsed ? (
        <aside
          className={cn(
            'flex w-11 shrink-0 flex-col items-center border-r py-2 gap-2',
            colors.border,
            colors.headerBg,
          )}
        >
          <ReferenceSidebarCollapseButton collapsed onToggle={() => setCollapsed(false)} />
          <span
            className={cn(
              'mt-2 max-h-40 overflow-hidden text-[10px] font-semibold uppercase tracking-wider [writing-mode:vertical-rl] rotate-180',
              colors.textMuted,
            )}
            title={eyebrow}
          >
            {eyebrow}
          </span>
        </aside>
      ) : (
        <aside
          className={cn('flex shrink-0 flex-col border-r', colors.border, colors.headerBg)}
          style={{ width: panelWidth }}
        >
          <div className={cn('flex items-start justify-between gap-2 border-b px-3 py-3', colors.border)}>
            <div className="min-w-0">
              <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                {eyebrow}
              </p>
              <p className={cn('mt-0.5 text-xs', colors.textMuted)}>Reference data</p>
            </div>
            <ReferenceSidebarCollapseButton collapsed={false} onToggle={() => setCollapsed(true)} />
          </div>
          <nav
            className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2"
            aria-label={`${eyebrow} reference data`}
          >
            {navSections.map((navSection, sectionIndex) => (
              <div key={navSection.group ?? `ungrouped-${sectionIndex}`} className="flex flex-col gap-0.5">
                {navSection.group ? (
                  <p
                    className={cn(
                      'px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider',
                      sectionIndex === 0 ? 'pt-1' : 'pt-3',
                      colors.textMuted,
                    )}
                  >
                    {navSection.group}
                  </p>
                ) : null}
                {navSection.items.map((item) => {
                  const active = section === item.id;
                  const compact = Boolean(item.group);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSection(item.id)}
                      className={cn(
                        'rounded-lg px-3 text-left transition',
                        compact ? 'py-1.5' : 'py-2.5',
                        active
                          ? cn(
                              colors.surface,
                              colors.primaryFg,
                              'shadow-sm ring-1',
                              isDark ? 'ring-white/15' : 'ring-black/10',
                            )
                          : cn(colors.textMuted, colors.interactiveHover),
                      )}
                    >
                      <span className={cn('block text-sm font-medium', active ? '' : colors.text)}>
                        {item.label}
                      </span>
                      {!compact && item.description ? (
                        <span
                          className={cn(
                            'mt-0.5 block text-[11px] leading-snug',
                            active ? 'opacity-75' : colors.textMuted,
                          )}
                        >
                          {item.description}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>
      )}

      {!collapsed && (
        <ReferenceSidebarSplitter
          panelWidth={panelWidth}
          minWidth={minWidth}
          maxWidth={maxWidth}
          resizing={panelResizing}
          onMouseDown={startResize}
          label="Resize reference sidebar"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div
          className={cn(
            'flex shrink-0 items-center justify-between gap-4 border-b px-5 py-3.5',
            colors.border,
          )}
        >
          <div className="min-w-0">
            <h2 className={cn('text-lg font-semibold tracking-tight', colors.text)}>
              {activeMeta?.label}
            </h2>
            {activeMeta?.description ? (
              <p className={cn('mt-0.5 text-sm', colors.textMuted)}>{activeMeta.description}</p>
            ) : null}
          </div>
          {collapsed && (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition',
                colors.border,
                colors.textMuted,
                colors.interactiveHover,
              )}
            >
              Show nav
            </button>
          )}
        </div>

        <div className={cn('min-h-0 flex-1', fillHeight ? 'overflow-hidden' : 'overflow-y-auto')}>
          <div className={cn(fillHeight ? 'h-full min-h-0' : 'p-4 sm:p-5')}>
            {renderPanel(section)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferenceCategoryWorkspace;
